import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaDna } from 'react-icons/fa6';
import { FiActivity, FiCheckCircle, FiClock, FiLayers, FiLink2, FiSearch } from 'react-icons/fi';
import { apiUrl } from '../utils/api';
import { buildBiologiaMolecularClosurePath, buildBiologiaMolecularDetailPath } from '../utils/biologiaMolecular';

const BM_DEBUG_PREFIX = '[BM DEBUG]';

const normalizeText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const formatDate = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const parsed = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('es-AR');
};

const formatNumber = (value) => new Intl.NumberFormat('es-AR').format(value || 0);

const getTargets = (assay) => {
  if (Array.isArray(assay?.patogenos_blanco) && assay.patogenos_blanco.length > 0) {
    return assay.patogenos_blanco.filter(Boolean);
  }

  if (!assay?.patogeno_blanco) {
    return [];
  }

  return assay.patogeno_blanco
    .split(/[;,]+/)
    .map((target) => target.trim())
    .filter(Boolean);
};

const getValidationLabel = (assay) =>
  assay?.fecha_validacion || assay?.validado_por ? 'Validado' : 'Pendiente';

const summarizeAssaysForDebug = (assays = []) =>
  assays.slice(0, 12).map((assay) => ({
    casos: Array.isArray(assay?.casos) ? assay.casos : [],
    ensayo_numero: assay?.ensayo_numero || '',
    fecha_ensayo: assay?.fecha_ensayo || '',
    muestras: Array.isArray(assay?.muestras) ? assay.muestras : [],
    numero_muestras: assay?.numero_muestras ?? '',
    patogenos_blanco: Array.isArray(assay?.patogenos_blanco) ? assay.patogenos_blanco : [],
    protocolo_general: assay?.protocolo_general || '',
    tipo_ensayo: assay?.tipo_ensayo || '',
  }));

const summarizeAxiosErrorForDebug = (error) => ({
  data: error?.response?.data,
  message: error?.message,
  method: error?.config?.method,
  status: error?.response?.status,
  url: error?.config?.url,
});

const StatCard = ({ accent = 'blue', description, icon: Icon, label, value }) => (
  <article className="dashboard-stat-card">
    <div className="dashboard-stat-card__top">
      <div className="dashboard-stat-card__title-group">
        <span className={`dashboard-stat-card__icon dashboard-stat-card__icon--${accent}`} aria-hidden="true">
          <Icon />
        </span>
        <span className="dashboard-stat-card__label">{label}</span>
      </div>
    </div>
    <strong className="dashboard-stat-card__value">{formatNumber(value)}</strong>
    <span className="dashboard-stat-card__hint">{description}</span>
  </article>
);

const BiologiaMolecularDashboardPage = () => {
  const [assays, setAssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('todos');
  const [diagnosticFilter, setDiagnosticFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('todos');

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError('');
    const listUrl = apiUrl('/api/biologia-molecular/');
    console.log(BM_DEBUG_PREFIX, 'Solicitando listado para dashboard de biologia molecular', { listUrl });

    axios
      .get(listUrl)
      .then((response) => {
        if (!active) {
          return;
        }

        const normalizedAssays = Array.isArray(response.data) ? response.data : [];
        console.log(BM_DEBUG_PREFIX, 'Respuesta del dashboard de biologia molecular', {
          firstAssays: summarizeAssaysForDebug(normalizedAssays),
          isArray: Array.isArray(response.data),
          total: normalizedAssays.length,
        });
        setAssays(normalizedAssays);
      })
      .catch((error) => {
        console.log(BM_DEBUG_PREFIX, 'Fallo al cargar listado del dashboard de biologia molecular', summarizeAxiosErrorForDebug(error));
        if (!active) {
          return;
        }

        setError('No pudimos cargar los ensayos de biología molecular en este momento.');
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const assayTypes = useMemo(
    () =>
      [...new Set(assays.map((assay) => assay.tipo_ensayo).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, 'es')),
    [assays],
  );

  const diagnostics = useMemo(
    () =>
      [...new Set(assays.flatMap((assay) => getTargets(assay)))]
        .sort((left, right) => left.localeCompare(right, 'es')),
    [assays],
  );

  const filteredAssays = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);

    return assays.filter((assay) => {
      const targets = getTargets(assay);
      const assayStatus = getValidationLabel(assay);
      const searchIndex = normalizeText([
        assay.ensayo_numero,
        assay.protocolo_general,
        assay.tipo_ensayo,
        assay.responsable_nombre || assay.responsable,
        assay.validado_por_nombre || assay.validado_por,
        assay.origen_muestras,
        assay.matriz_tejido,
        ...(Array.isArray(assay.casos) ? assay.casos : []),
        ...(Array.isArray(assay.muestras) ? assay.muestras : []),
        ...targets,
      ].join(' '));

      const matchesSearch = !normalizedSearch || searchIndex.includes(normalizedSearch);
      const matchesType = typeFilter === 'todos' || assay.tipo_ensayo === typeFilter;
      const matchesDiagnostic = diagnosticFilter === 'todos' || targets.includes(diagnosticFilter);
      const matchesStatus = statusFilter === 'todos' || assayStatus === statusFilter;

      return matchesSearch && matchesType && matchesDiagnostic && matchesStatus;
    });
  }, [assays, diagnosticFilter, searchTerm, statusFilter, typeFilter]);

  const dashboardMetrics = useMemo(() => {
    const totalSamples = assays.reduce((sum, assay) => sum + Number(assay.numero_muestras || 0), 0);
    const validated = assays.filter((assay) => getValidationLabel(assay) === 'Validado').length;
    const multiplex = assays.filter((assay) => getTargets(assay).length > 1).length;
    const linkedCases = new Set(
      assays.flatMap((assay) => (Array.isArray(assay.casos) ? assay.casos : []).filter(Boolean)),
    ).size;

    return {
      linkedCases,
      multiplex,
      pending: assays.length - validated,
      totalAssays: assays.length,
      totalSamples,
      validated,
    };
  }, [assays]);

  const latestAssayDate = useMemo(() => {
    const datedAssays = assays
      .map((assay) => assay.fecha_ensayo)
      .filter(Boolean)
      .sort((left, right) => right.localeCompare(left));

    return datedAssays[0] ? formatDate(datedAssays[0]) : 'Sin fecha';
  }, [assays]);

  const visibleCasesCount = useMemo(
    () =>
      new Set(
        filteredAssays.flatMap((assay) => (Array.isArray(assay.casos) ? assay.casos : []).filter(Boolean)),
      ).size,
    [filteredAssays],
  );

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('todos');
    setDiagnosticFilter('todos');
    setStatusFilter('todos');
  };

  useEffect(() => {
    console.log(BM_DEBUG_PREFIX, 'Estado actual del dashboard de biologia molecular', {
      assayIds: assays.slice(0, 20).map((assay) => assay.ensayo_numero),
      diagnosticFilter,
      filteredIds: filteredAssays.slice(0, 20).map((assay) => assay.ensayo_numero),
      filteredTotal: filteredAssays.length,
      loading,
      searchTerm,
      statusFilter,
      totalAssays: assays.length,
      typeFilter,
    });
  }, [assays, diagnosticFilter, filteredAssays, loading, searchTerm, statusFilter, typeFilter]);

  return (
    <section className="container-fluid page-shell dashboard-page bm-dashboard-page" aria-busy={loading}>
      {loading ? (
        <div className="dashboard-page-loading-overlay" role="status" aria-live="polite">
          <div className="dashboard-page-loading-overlay__card">
            <span className="inline-loader__spinner" aria-hidden="true" />
            <strong>Cargando dashboard...</strong>
            <span>Estamos reuniendo los ensayos, métricas y filtros de biología molecular.</span>
          </div>
        </div>
      ) : null}

      <div className="page-header">
        <div className="dashboard-page-header__content">
          <div>
            <span className="section-kicker">Biología molecular</span>
            <h2 className="page-title">Dashboard de ensayos</h2>
            <p className="page-subtitle">
              Revisa ensayos cargados, su estado de validación y los diagnósticos asociados desde una sola vista.
            </p>
          </div>
          <div className="dashboard-header-highlights">
            <span className="dashboard-header-pill">
              <FiActivity aria-hidden="true" />
              {formatNumber(filteredAssays.length)} ensayos visibles
            </span>
            <span className="dashboard-header-pill">
              <FiLayers aria-hidden="true" />
              {formatNumber(diagnostics.length)} diagnósticos cargados
            </span>
            <span className="dashboard-header-pill">
              <FiClock aria-hidden="true" />
              Último ensayo: {latestAssayDate}
            </span>
          </div>
        </div>

        <div className="bm-dashboard-header__actions">
          <Link className="btn btn-primary bm-dashboard-header__button" to="/biologia-molecular">
            Cargar ensayo
          </Link>
          <div className="molecular-page-badge bm-dashboard-header__badge" aria-hidden="true">
            <FaDna />
          </div>
        </div>
      </div>

      <div className="dashboard-stats dashboard-stats--expanded row g-3">
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="blue"
            description="Ensayos registrados en biología molecular."
            icon={FiActivity}
            label="Ensayos totales"
            value={dashboardMetrics.totalAssays}
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="sky"
            description="Corridas que ya tienen validación informada."
            icon={FiCheckCircle}
            label="Ensayos validados"
            value={dashboardMetrics.validated}
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="slate"
            description="Ensayos todavía pendientes de validación."
            icon={FiClock}
            label="Pendientes"
            value={dashboardMetrics.pending}
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="blue"
            description="Suma del número de muestras declaradas."
            icon={FiLayers}
            label="Muestras declaradas"
            value={dashboardMetrics.totalSamples}
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="sky"
            description="Ensayos con más de un diagnóstico o blanco."
            icon={FaDna}
            label="Ensayos multiplex"
            value={dashboardMetrics.multiplex}
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            accent="slate"
            description="Casos distintos vinculados a los ensayos cargados."
            icon={FiLink2}
            label="Casos vinculados"
            value={dashboardMetrics.linkedCases}
          />
        </div>
      </div>

      <div className="surface-card case-list-card bm-dashboard-filter-card">
        <div className="dashboard-filter-toolbar bm-dashboard-filter-toolbar">
          <div className="dashboard-filter-toolbar__controls">
            <div className="dashboard-filter-toolbar__heading">
              <span className="section-kicker">Filtros</span>
              <h3 className="form-title">Encuentra un ensayo rápido</h3>
              <p className="page-subtitle dashboard-filter-toolbar__copy">
                Busca por número de ensayo, caso, muestra, responsable o diagnóstico y ajusta la vista por tipo y estado.
              </p>
            </div>

            <div className="bm-dashboard-filters row g-3">
              <div className="col-12 col-lg-5">
                <label className="form-label bm-dashboard-field-label" htmlFor="bm-dashboard-search">
                  Buscar ensayo
                </label>
                <div className="bm-dashboard-search">
                  <span className="bm-dashboard-search__icon" aria-hidden="true">
                    <FiSearch />
                  </span>
                  <input
                    id="bm-dashboard-search"
                    className="form-control bm-dashboard-search__input"
                    placeholder="Ej. BM-2026-015, caso, muestra o Campylobacter"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label bm-dashboard-field-label" htmlFor="bm-dashboard-type">
                  Tipo de ensayo
                </label>
                <select
                  id="bm-dashboard-type"
                  className="form-select"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value="todos">Todos</option>
                  {assayTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-2">
                <label className="form-label bm-dashboard-field-label" htmlFor="bm-dashboard-status">
                  Estado
                </label>
                <select
                  id="bm-dashboard-status"
                  className="form-select"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="Validado">Validado</option>
                  <option value="Pendiente">Pendiente</option>
                </select>
              </div>

              <div className="col-12 col-lg-2">
                <label className="form-label bm-dashboard-field-label" htmlFor="bm-dashboard-diagnostic">
                  Diagnóstico
                </label>
                <select
                  id="bm-dashboard-diagnostic"
                  className="form-select"
                  value={diagnosticFilter}
                  onChange={(event) => setDiagnosticFilter(event.target.value)}
                >
                  <option value="todos">Todos</option>
                  {diagnostics.map((diagnostic) => (
                    <option key={diagnostic} value={diagnostic}>
                      {diagnostic}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="dashboard-filter-toolbar__summary bm-dashboard-filter-summary">
            <div className="bm-dashboard-summary-grid">
              <div className="bm-dashboard-summary-tile">
                <span className="bm-dashboard-summary-tile__label">Visibles</span>
                <strong>{formatNumber(filteredAssays.length)}</strong>
              </div>
              <div className="bm-dashboard-summary-tile">
                <span className="bm-dashboard-summary-tile__label">Casos visibles</span>
                <strong>{formatNumber(visibleCasesCount)}</strong>
              </div>
              <div className="bm-dashboard-summary-tile">
                <span className="bm-dashboard-summary-tile__label">Diagnósticos</span>
                <strong>{formatNumber(diagnostics.length)}</strong>
              </div>
            </div>

            <div className="dashboard-filter-toolbar__actions">
              <button
                type="button"
                className="btn btn-outline-secondary dashboard-filter-toolbar__button"
                onClick={clearFilters}
                disabled={!searchTerm && typeFilter === 'todos' && diagnosticFilter === 'todos' && statusFilter === 'todos'}
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="surface-card case-list-card bm-dashboard-table-card">
        <div className="bm-dashboard-table-card__header">
          <div>
            <span className="section-kicker">Ensayos</span>
            <h3 className="form-title">Listado de biología molecular</h3>
          </div>
          <span className="bm-dashboard-table-card__count">{formatNumber(filteredAssays.length)} registros</span>
        </div>

        {error ? <div className="alert subtle-alert mb-0">{error}</div> : null}

        {!loading && !error && filteredAssays.length === 0 ? (
          <div className="dashboard-empty">
            No encontramos ensayos con los filtros actuales. Prueba limpiando la búsqueda o cambiando el diagnóstico.
          </div>
        ) : (
          <div className="case-table-responsive">
            <table className="table align-middle bm-dashboard-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Ensayo</th>
                  <th scope="col">Fecha</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Diagnóstico(s)</th>
                  <th scope="col">Casos</th>
                  <th scope="col">Muestras</th>
                  <th scope="col">Responsable</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="text-end">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssays.map((assay) => {
                  const targets = getTargets(assay);
                  const validationLabel = getValidationLabel(assay);
                  const linkedCases = Array.isArray(assay.casos) ? assay.casos.filter(Boolean) : [];
                  const linkedSamples = Array.isArray(assay.muestras) ? assay.muestras.filter(Boolean) : [];

                  return (
                    <tr key={assay.ensayo_numero}>
                      <td>
                        <div className="bm-dashboard-cell-stack">
                          <Link className="bm-dashboard-ensayo-link" to={buildBiologiaMolecularDetailPath(assay.ensayo_numero)}>
                            {assay.ensayo_numero}
                          </Link>
                          <span className="bm-dashboard-cell-meta">
                            {assay.protocolo_general || linkedCases[0] || 'Sin caso principal'}
                          </span>
                        </div>
                      </td>
                      <td>{formatDate(assay.fecha_ensayo)}</td>
                      <td>{assay.tipo_ensayo || 'Sin tipo'}</td>
                      <td>
                        <div className="bm-dashboard-chip-list">
                          {targets.length > 0 ? (
                            targets.map((target) => (
                              <span className="bm-dashboard-chip" key={`${assay.ensayo_numero}-${target}`}>
                                {target}
                              </span>
                            ))
                          ) : (
                            <span className="bm-dashboard-cell-meta">Sin diagnóstico</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="bm-dashboard-cell-stack">
                          <strong>{linkedCases.length}</strong>
                          <span className="bm-dashboard-cell-meta">
                            {linkedCases.length > 0 ? linkedCases.join(', ') : 'Sin casos vinculados'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="bm-dashboard-cell-stack">
                          <strong>{formatNumber(assay.numero_muestras || linkedSamples.length)}</strong>
                          <span className="bm-dashboard-cell-meta">
                            {linkedSamples.length > 0 ? linkedSamples.join(', ') : 'Sin muestras vinculadas'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="bm-dashboard-cell-stack">
                          <span>{assay.responsable_nombre || assay.responsable || 'Sin responsable'}</span>
                          <span className="bm-dashboard-cell-meta">
                            {assay.validado_por_nombre || assay.validado_por || 'Sin validación informada'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`bm-dashboard-status-chip${
                            validationLabel === 'Validado'
                              ? ' bm-dashboard-status-chip--ok'
                              : ' bm-dashboard-status-chip--pending'
                          }`}
                        >
                          {validationLabel}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex flex-wrap justify-content-end gap-2">
                          <Link className="btn btn-sm btn-outline-primary" to={buildBiologiaMolecularDetailPath(assay.ensayo_numero)}>
                            Abrir
                          </Link>
                          <Link className="btn btn-sm btn-primary" to={buildBiologiaMolecularClosurePath(assay.ensayo_numero)}>
                            Cierre
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default BiologiaMolecularDashboardPage;
